import { FC, useEffect, useMemo, useState } from "react";
import { InputCommonProps } from "./Common";
import styles from "./RangeSlider.module.scss";
import { Select } from "./Select";
import { numberGenerator } from "../../Utilities";

interface RangeSliderProps {
    min: number;
    max: number;
    values: MinMax;
    onChange: (val: MinMax) => void;
    lowProps?: React.InputHTMLAttributes<HTMLInputElement & HTMLSelectElement>;
    highProps?: React.InputHTMLAttributes<HTMLInputElement & HTMLSelectElement>;
    showSelects?: boolean;
    itemDescription?: (n: number) => string;
}

interface MinMax {
    low: number;
    high: number;
}

// CREDIT: https://css-tricks.com/multi-thumb-sliders-particular-two-thumb-case/

export const RangeSlider: FC<RangeSliderProps & InputCommonProps> = (props) => {
    const [innerMin, setInnerMin] = useState<number>(props.values.low);
    const [innerMax, setInnerMax] = useState<number>(props.values.high);

    const generatedNumbers = useMemo(() => {
        return numberGenerator(props.min, props.max);
    }, [props.min, props.max]);

    useEffect(() => {
        setInnerMin(props.values.low);
        setInnerMax(props.values.high);
    }, [props.values.low, props.values.high]);

    useEffect(() => {
        const debounce = setTimeout(async () => {
            props.onChange({
                low: innerMin < innerMax ? innerMin : innerMax,
                high: innerMin < innerMax ? innerMax : innerMin
            });
        }, 200);

        return () => clearTimeout(debounce);
    }, [innerMin, innerMax]);

    const itemDescription = props.itemDescription ?? (n => n.toString());

    return (
        <>
            <div className={styles.wrap} style={{ "--a": innerMin, "--b": innerMax, "--min": props.min, "--max": props.max } as React.CSSProperties}>
                <input type="range" className={styles.multiRange} min={props.min} value={innerMin} max={props.max} onInput={e => setInnerMin(parseInt(e.currentTarget.value))} {...props.lowProps} />
                <input type="range" className={styles.multiRange} min={props.min} value={innerMax} max={props.max} onInput={e => setInnerMax(parseInt(e.currentTarget.value))} {...props.lowProps} />
                {(props.showSelects ?? true) && (
                    <>
                        <div className={styles.selectContainer}>
                            <Select
                                id="minAge"
                                items={generatedNumbers}
                                values={n => n.toString()}
                                descriptions={itemDescription}
                                value={innerMin}
                                onChange={e => setInnerMin(parseInt(e.target.value))}
                                validations={{ required: true }}
                                {...props.lowProps}
                            />
                        </div>
                        <div className={styles.selectContainer}>
                            <Select
                                id="maxAge"
                                items={generatedNumbers}
                                values={n => n.toString()}
                                descriptions={itemDescription}
                                value={innerMax}
                                onChange={e => setInnerMax(parseInt(e.target.value))}
                                validations={{ required: true }}
                                {...props.highProps}
                            />
                        </div>
                    </>
                )}
            </div>
        </>
    );
};